import React, { useMemo, useState } from 'react'
import {
  getCustomerDetails,
  updateInfoStep,
  updateSupplementalQuestions,
} from '../../../../service/customer'
import { useNavigate, useSearchParams } from 'react-router-dom'

import BottomStepNavigator from '../../BottomStepNavigator.component'
import BouncingDotsLoader from '../../../common/Loading/BouncingDotsLoader'
import { CurrentPlanISComponentEnum } from '../../../../enums/CurrentPlanISComponentEnum'
import DateQuestion from './DateQuestion'
import DisplayCard from './DisplayCard'
import { InfoStepEnum } from '../../../../enums/InfoStepEnum'
import { MedicareConstants } from '../../../../constants/MedicareConstants'
import OptionalQuoteQuestion from './OptionalQuoteQuestion'
import Question from '../Common/Question'
import RecommendedPlan from './RecommendedPlan'
import RouteConstants from '../../../../constants/RouteConstants'
import SecureComponent from '../../../../pages/common/SecureComponent'
import SelectAdvantagePlan from './SelectAdvantagePlan'
import SelectAdvantagePlanProvider from './SelectAdvantagePlanProvider'
import SelectGroupOrIHPlanProvider from './SelectGroupOrIHPlanProvider'
import SelectGroupOrIHPlanType from './SelectGroupOrIHPlanType'
import SelectMABenefits from './SelectMABenefits'
import SelectPlanType from './SelectPlanType'
import SelectSupplementPlan from './SelectSupplementPlan'
import SelectSupplementPlanType from './SelectSupplementPlanType'
import { SnackbarTypes } from '../../../../enums/SnackbarTypesEnum'
import StringConstants from '../../../../constants/StringConstants'
import { addResponsesForQuestionnaires } from '../../../../service/questionnaire'
import currentPlanStore from '../../../../datastore/CurrentPlanStore'
import customerStore from '../../../../datastore/CustomerStore'
import { getApiErrorMessage } from '../../../../utils/StringUtils'
import { isEmpty } from 'lodash'
import mPartDPlansFilterStore from '../../../../datastore/medicareQuotes/MPartPlanFilterQuotesStore'
import medicareAdvantageFilterQuoteStore from '../../../../datastore/medicareQuotes/MedicareAdvantageFilterQuoteStore'
import moment from 'moment'
import msPlanFiltersStore from '../../../../datastore/medicareQuotes/MSupplementPlanFiltersStore'
import { observer } from 'mobx-react'
import snackbarStore from '../../../../datastore/SnackbarStore'
import styled from '@emotion/styled'

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  @media screen and (max-width: 600px) {
    width: 100%;
  }
`

const calculateTotalSteps = (
  forSpouse: boolean,
  quoteType?: string
): number => {
  const baseSteps = 4 // OPTIONAL_QUOTE_QUESTION, QUESTION_1, QUESTION_2/3, QUESTION_4

  if (
    !quoteType ||
    quoteType === MedicareConstants.INSURANCE_TYPE.AB ||
    quoteType === MedicareConstants.INSURANCE_TYPE.Medicaid ||
    quoteType === MedicareConstants.INSURANCE_TYPE.Tricare ||
    quoteType === MedicareConstants.INSURANCE_TYPE.None
  ) {
    // Flow ends at QUESTION_4
    return forSpouse ? baseSteps + 4 : baseSteps // +4 for spouse questions if applicable
  }

  switch (quoteType) {
    case MedicareConstants.INSURANCE_TYPE.MA:
      // QUESTION_5 -> QUESTION_6 -> QUESTION_11 (3 additional steps)
      return forSpouse ? baseSteps + 3 + 4 : baseSteps + 3

    case MedicareConstants.INSURANCE_TYPE.MS:
      // QUESTION_7 -> QUESTION_8 (2 additional steps)
      return forSpouse ? baseSteps + 2 + 4 : baseSteps + 2

    case MedicareConstants.INSURANCE_TYPE.IH:
    case MedicareConstants.INSURANCE_TYPE.Group:
      // QUESTION_9 -> QUESTION_10 (2 additional steps)
      return forSpouse ? baseSteps + 2 + 4 : baseSteps + 2

    default:
      // QUESTION_10 (1 additional step)
      return forSpouse ? baseSteps + 1 + 4 : baseSteps + 1
  }
}

const getCurrentStepNumber = (
  currentStep: CurrentPlanISComponentEnum,
  forSpouse: boolean,
  displayCard: boolean
): number => {
  const stepMapping: Record<CurrentPlanISComponentEnum, number> = {
    [CurrentPlanISComponentEnum.OPTIONAL_QUOTE_QUESTION]: forSpouse ? 9 : 1,
    [CurrentPlanISComponentEnum.DISPLAY_CARD]: forSpouse ? 10 : 2,
    [CurrentPlanISComponentEnum.QUESTION_1]: forSpouse
      ? displayCard
        ? 11
        : 10
      : displayCard
      ? 3
      : 2,
    [CurrentPlanISComponentEnum.QUESTION_2]: forSpouse
      ? displayCard
        ? 12
        : 11
      : displayCard
      ? 4
      : 3,
    [CurrentPlanISComponentEnum.QUESTION_3]: forSpouse
      ? displayCard
        ? 12
        : 11
      : displayCard
      ? 4
      : 3,
    [CurrentPlanISComponentEnum.QUESTION_4]: forSpouse
      ? displayCard
        ? 13
        : 12
      : displayCard
      ? 5
      : 4,
    [CurrentPlanISComponentEnum.QUESTION_5]: forSpouse
      ? displayCard
        ? 14
        : 13
      : displayCard
      ? 6
      : 5,
    [CurrentPlanISComponentEnum.QUESTION_6]: forSpouse
      ? displayCard
        ? 15
        : 14
      : displayCard
      ? 7
      : 6,
    [CurrentPlanISComponentEnum.QUESTION_7]: forSpouse
      ? displayCard
        ? 14
        : 13
      : displayCard
      ? 6
      : 5,
    [CurrentPlanISComponentEnum.QUESTION_8]: forSpouse
      ? displayCard
        ? 15
        : 14
      : displayCard
      ? 7
      : 6,
    [CurrentPlanISComponentEnum.QUESTION_9]: forSpouse
      ? displayCard
        ? 14
        : 13
      : displayCard
      ? 6
      : 5,
    [CurrentPlanISComponentEnum.QUESTION_10]: forSpouse
      ? displayCard
        ? 15
        : 14
      : displayCard
      ? 7
      : 6,
    [CurrentPlanISComponentEnum.QUESTION_11]: forSpouse
      ? displayCard
        ? 16
        : 15
      : displayCard
      ? 8
      : 7,
    [CurrentPlanISComponentEnum.FINISHED]: 0, // Not used in navigation
  }

  return stepMapping[currentStep] || 1
}

const CurrentPlan: React.FC = () => {
  const navigate = useNavigate()
  const [disableNext, setDisableNext] = React.useState(true)
  const [currentPlanStep, setCurrentPlanStep] =
    React.useState<CurrentPlanISComponentEnum>(
      CurrentPlanISComponentEnum.OPTIONAL_QUOTE_QUESTION
    )

  const [optionalQuotes, setOptionalQuotes] = useState()
  const [loading, setLoading] = React.useState(true)

  const [state, setState] = React.useState({
    nextButtonLoading: false,
  })

  const [displayCard, setDisplayCard] = React.useState<boolean>(false)

  const [effectiveDate, setEffectiveDate] = React.useState('')
  const [hasNext, setHasNext] = React.useState<boolean>(true)
  const [forSpouse, setForSpouse] = React.useState(false)
  const [searchParams] = useSearchParams()
  const [sectionNumber, setSectionNumber] = useState<number>(1)

  const quoteType = !forSpouse
    ? currentPlanStore.get().quoteType
    : currentPlanStore.spouseQuoteType

  const totalSteps = useMemo(
    () => calculateTotalSteps(forSpouse, quoteType),
    [forSpouse, quoteType]
  )

  const currentStepNumber = useMemo(
    () => getCurrentStepNumber(currentPlanStep, forSpouse, displayCard),
    [currentPlanStep, forSpouse, displayCard]
  )

  React.useEffect(() => {
    if (searchParams.get('forSpouse') === 'true') {
      setForSpouse(true)
      setSectionNumber(9)
      setCurrentPlanStep(CurrentPlanISComponentEnum.QUESTION_1)
    } else {
      setForSpouse(false)
    }
  }, [searchParams])

  const handleQuestionChange = (
    question: CurrentPlanISComponentEnum,
    isNext: boolean
  ) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setCurrentPlanStep(question)
  }

  const disableMainNext = () => {
    setDisableNext(true)
  }
  const enableMainNext = () => {
    setDisableNext(false)
  }

  const handleHasNext = (value: boolean) => {
    setHasNext(value)
  }

  const updateCurrentPlanStep = async () => {
    try {
      customerStore.setIsProfileUpdating(true)
      setState({
        ...state,
        nextButtonLoading: true,
      })
      if (customerStore.getSpouseOptIn() === false || forSpouse) {
        await updateInfoStep(InfoStepEnum.CURRENT_PLAN)
        snackbarStore.set({
          snackbarMessage: StringConstants.SNACKBAR_SAVE_SUCCESS_MSG,
          snackbarOpen: true,
          snackbarType: SnackbarTypes.SUCCESS,
        })
        setDisplayCard(false)
        setCurrentPlanStep(CurrentPlanISComponentEnum.FINISHED)
      } else if (customerStore.getSpouseOptIn() && !forSpouse) {
        navigate(`${RouteConstants.INFO_STEPS_CURRENT_PLAN}/?forSpouse=true`)
      }
    } catch (error) {
      snackbarStore.set({
        snackbarMessage: 'Something went wrong',
        snackbarOpen: true,
        snackbarType: 'error',
      })
    } finally {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      customerStore.setIsProfileUpdating(false)
      setState({
        ...state,
        nextButtonLoading: false,
      })
    }
  }

  const handleEdit = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_1, true)
  }

  const getHandleNext = (componentName: CurrentPlanISComponentEnum) => {
    const quoteType = !forSpouse
      ? currentPlanStore.get().quoteType
      : currentPlanStore.spouseQuoteType
    if (
      componentName === CurrentPlanISComponentEnum.QUESTION_4 &&
      (quoteType === MedicareConstants.INSURANCE_TYPE.AB ||
        quoteType === MedicareConstants.INSURANCE_TYPE.Medicaid ||
        quoteType === MedicareConstants.INSURANCE_TYPE.Tricare ||
        quoteType === MedicareConstants.INSURANCE_TYPE.None ||
        isEmpty(quoteType))
    ) {
      return handleCurrentPlanFinish()
    }

    const handlers: Record<CurrentPlanISComponentEnum, () => void> = {
      [CurrentPlanISComponentEnum.OPTIONAL_QUOTE_QUESTION]: () => {
        if (displayCard) {
          handleQuestionChange(CurrentPlanISComponentEnum.DISPLAY_CARD, true)
        } else handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_1, true)
      },
      [CurrentPlanISComponentEnum.DISPLAY_CARD]: () => {
        updateCurrentPlanStep()
      },
      [CurrentPlanISComponentEnum.QUESTION_1]: () => {},
      [CurrentPlanISComponentEnum.QUESTION_2]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_4, true),
      [CurrentPlanISComponentEnum.QUESTION_3]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_4, true),
      [CurrentPlanISComponentEnum.QUESTION_4]: () => {
        // const quoteType = currentPlanStore.get().quoteType
        switch (quoteType) {
          case MedicareConstants.INSURANCE_TYPE.MA:
            return handleQuestionChange(
              CurrentPlanISComponentEnum.QUESTION_5,
              true
            )
          case MedicareConstants.INSURANCE_TYPE.MS:
            return handleQuestionChange(
              CurrentPlanISComponentEnum.QUESTION_7,
              true
            )
          case MedicareConstants.INSURANCE_TYPE.IH:
          case MedicareConstants.INSURANCE_TYPE.Group:
            return handleQuestionChange(
              CurrentPlanISComponentEnum.QUESTION_9,
              true
            )
          default:
            return handleQuestionChange(
              CurrentPlanISComponentEnum.QUESTION_10,
              true
            )
        }
      },
      [CurrentPlanISComponentEnum.QUESTION_5]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_6, true),
      [CurrentPlanISComponentEnum.QUESTION_6]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_11, true),
      [CurrentPlanISComponentEnum.QUESTION_7]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_8, true),
      [CurrentPlanISComponentEnum.QUESTION_8]: () => handleCurrentPlanFinish(),
      [CurrentPlanISComponentEnum.QUESTION_9]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_10, true),
      [CurrentPlanISComponentEnum.QUESTION_10]: () => handleCurrentPlanFinish(),
      [CurrentPlanISComponentEnum.QUESTION_11]: () => handleCurrentPlanFinish(),
      [CurrentPlanISComponentEnum.FINISHED]: () => {},
    }

    const handler = handlers[componentName]
    if (handler) {
      return handler()
    }
  }

  const updateOptionalQuotes = () => {
    setState({ ...state, nextButtonLoading: true })
    if (optionalQuotes)
      addResponsesForQuestionnaires(forSpouse, optionalQuotes)
        .then(() => {
          getHandleNext(currentPlanStep)
          setState({ ...state, nextButtonLoading: false })
          snackbarStore.set({
            snackbarOpen: true,
            snackbarMessage: 'Saved Successfully',
            snackbarType: SnackbarTypes.SUCCESS,
          })
        })
        .catch((err) => {
          snackbarStore.set({
            snackbarOpen: true,
            snackbarMessage: 'Unable to fetch the questionnaire',
            snackbarType: SnackbarTypes.ERROR,
          })
        })
    else {
      setState({ ...state, nextButtonLoading: false })
      snackbarStore.set({
        snackbarOpen: true,
        snackbarMessage: 'Invalid Input',
        snackbarType: SnackbarTypes.ERROR,
      })
    }
  }

  const getHandleBack = (componentName: CurrentPlanISComponentEnum) => {
    const handlers: Record<CurrentPlanISComponentEnum, () => void> = {
      [CurrentPlanISComponentEnum.OPTIONAL_QUOTE_QUESTION]: () =>
        navigate(RouteConstants.INFO_STEPS_PRESCRIPTION),
      [CurrentPlanISComponentEnum.QUESTION_1]: () => {
        if (displayCard)
          handleQuestionChange(CurrentPlanISComponentEnum.DISPLAY_CARD, false)
        else
          handleQuestionChange(
            CurrentPlanISComponentEnum.OPTIONAL_QUOTE_QUESTION,
            false
          )
      },
      [CurrentPlanISComponentEnum.DISPLAY_CARD]: () =>
        handleQuestionChange(
          CurrentPlanISComponentEnum.OPTIONAL_QUOTE_QUESTION,
          false
        ),
      [CurrentPlanISComponentEnum.QUESTION_2]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_1, false),
      [CurrentPlanISComponentEnum.QUESTION_3]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_1, false),
      [CurrentPlanISComponentEnum.QUESTION_4]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_2, false),
      [CurrentPlanISComponentEnum.QUESTION_5]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_4, false),
      [CurrentPlanISComponentEnum.QUESTION_6]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_5, false),
      [CurrentPlanISComponentEnum.QUESTION_7]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_4, false),
      [CurrentPlanISComponentEnum.QUESTION_8]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_7, false),
      [CurrentPlanISComponentEnum.QUESTION_9]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_4, false),
      [CurrentPlanISComponentEnum.QUESTION_10]: () => {
        const quoteType = !forSpouse
          ? currentPlanStore.get().quoteType
          : currentPlanStore.spouseQuoteType
        return quoteType === MedicareConstants.INSURANCE_TYPE.IH ||
          quoteType === MedicareConstants.INSURANCE_TYPE.Group
          ? handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_9, false)
          : handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_4, false)
      },
      [CurrentPlanISComponentEnum.QUESTION_11]: () =>
        handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_6, false),
      [CurrentPlanISComponentEnum.FINISHED]: () => {},
    }

    const handler = handlers[componentName]

    if (handler) {
      return handler()
    }
  }

  const changeComponent = (componentName: CurrentPlanISComponentEnum) => {
    switch (componentName) {
      case CurrentPlanISComponentEnum.DISPLAY_CARD:
        return (
          <DisplayCard
            forSpouse={forSpouse}
            effectiveDate={effectiveDate}
            handleEdit={handleEdit}
          />
        )
      case CurrentPlanISComponentEnum.OPTIONAL_QUOTE_QUESTION:
        return (
          <OptionalQuoteQuestion
            forSpouse={forSpouse}
            enableMainNext={enableMainNext}
            disableMainNext={disableMainNext}
            handleHasNext={handleHasNext}
            optionalQuotes={optionalQuotes}
            setOptionalQuotes={setOptionalQuotes}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_1:
        return (
          <Question
            key={CurrentPlanISComponentEnum.QUESTION_1}
            heading={`${
              forSpouse ? 'Is your spouse/partner' : 'Are you'
            } currently enrolled in Medicare Part B?`}
            isRequired={true}
            description='Filters may be applied based on your current coverage to showcase plans most suitable for you. You can modify this anytime by removing the filters.'
            handleYes={() =>
              handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_2, true)
            }
            handleNo={() =>
              handleQuestionChange(CurrentPlanISComponentEnum.QUESTION_3, true)
            }
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            question={CurrentPlanISComponentEnum.QUESTION_1}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_2:
        return (
          <DateQuestion
            key={CurrentPlanISComponentEnum.QUESTION_2}
            forSpouse={forSpouse}
            question={`When did ${
              forSpouse ? 'your spouse/partner' : 'you'
            } enroll into Medicare Part B?`}
            description='If you aren’t sure, please make an estimate.'
            secondaryFunction={(date: string) => {
              if (!forSpouse)
                currentPlanStore.setEffectiveDate(
                  date ? (date === '' ? null : date) : null
                )
              else
                currentPlanStore.setSpouseEffectiveDate(
                  date ? (date === '' ? null : date) : null
                )
            }}
            dateFieldInputProps={{
              max: moment().add(1, 'year').year().toString() + '-12-31',
            }}
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            handleHasNext={handleHasNext}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_3:
        return (
          <DateQuestion
            key={CurrentPlanISComponentEnum.QUESTION_3}
            forSpouse={forSpouse}
            question={`When does your ${
              forSpouse ? "spouse/partner's" : ''
            } Medicare Part B take effect?`}
            description='If you aren’t sure, please make an estimate.'
            secondaryFunction={(date: string) => {
              if (!forSpouse)
                currentPlanStore.setEffectiveDate(
                  date ? (date === '' ? null : date) : null
                )
              else
                currentPlanStore.setSpouseEffectiveDate(
                  date ? (date === '' ? null : date) : null
                )
            }}
            dateFieldInputProps={{
              max: moment().add(1, 'year').year().toString() + '-12-31',
            }}
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            handleHasNext={handleHasNext}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_4:
        return (
          <SelectPlanType
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            handleHasNext={handleHasNext}
            forSpouse={forSpouse}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_5:
        return (
          <SelectAdvantagePlanProvider
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            handleHasNext={handleHasNext}
            forSpouse={forSpouse}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_6:
        return (
          <SelectAdvantagePlan
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            handleHasNext={handleHasNext}
            forSpouse={forSpouse}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_7:
        return (
          <SelectSupplementPlanType
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            handleHasNext={handleHasNext}
            forSpouse={forSpouse}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_8:
        return (
          <SelectSupplementPlan
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            handleHasNext={handleHasNext}
            forSpouse={forSpouse}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_9:
        return (
          <SelectGroupOrIHPlanType
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            handleHasNext={handleHasNext}
            forSpouse={forSpouse}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_10:
        return (
          <SelectGroupOrIHPlanProvider
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            handleHasNext={handleHasNext}
            forSpouse={forSpouse}
          />
        )
      case CurrentPlanISComponentEnum.QUESTION_11:
        return (
          <SelectMABenefits
            forSpouse={forSpouse}
            disableMainNext={disableMainNext}
            enableMainNext={enableMainNext}
            handleHasNext={handleHasNext}
          />
        )
    }
  }

  React.useEffect(() => {
    window.scroll({ top: 0, behavior: 'smooth' })
    currentPlanStore.reset()
    if (customerStore.infoSteps.includes('currentPlan')) {
      setCurrentPlanStep(CurrentPlanISComponentEnum.FINISHED)
      setLoading(false)
    } else {
      getCustomerDetails()
        .then((response) => {
          const data = response
          if (
            !forSpouse &&
            (data.currentMedicarePartBDateSelf ||
              data.currentPlanTypeSelf ||
              data.currentProviderSelf ||
              data.currentCoverageSelf ||
              !isEmpty(data.maPlanBenefits))
          ) {
            setDisplayCard(true)

            if (data.currentMedicarePartBDateSelf) {
              const parts = data.currentMedicarePartBDateSelf
                .substring(0, 10)
                .split('-')
              currentPlanStore.setEffectiveDate(
                data.currentMedicarePartBDateSelf
              )
              setEffectiveDate(`${parts[1]}-${parts[2]}-${parts[0]}`)
            }
            currentPlanStore.setPlanType(data.currentPlanTypeSelf)
            currentPlanStore.setProvider(data.currentProviderSelf)

            if (data.currentCoverageSelf) {
              currentPlanStore.setQuoteType(data.currentCoverageSelf)
              currentPlanStore.setCoveredByHealthInsurance(true)
            } else {
              currentPlanStore.setCoveredByHealthInsurance(false)
            }
            currentPlanStore.setMABenefits(data.maPlanBenefits)
            enableMainNext && enableMainNext()
          } else if (
            forSpouse &&
            (data.currentMedicarePartBDateSpouse ||
              data.currentPlanTypeSpouse ||
              data.currentProviderSpouse ||
              data.currentCoverageSpouse ||
              !isEmpty(data.spouseMAPlanBenefits))
          ) {
            setDisplayCard(true)

            if (data.currentMedicarePartBDateSpouse) {
              const parts = data.currentMedicarePartBDateSpouse
                .substring(0, 10)
                .split('-')
              currentPlanStore.setSpouseEffectiveDate(
                data.currentMedicarePartBDateSpouse
              )
              setEffectiveDate(`${parts[1]}-${parts[2]}-${parts[0]}`)
            }
            currentPlanStore.setSpousePlanType(data.currentPlanTypeSpouse)
            currentPlanStore.setSpouseProvider(data.currentProviderSpouse)
            currentPlanStore.setSpouseMABenefits(data.spouseMAPlanBenefits)

            if (data.currentCoverageSpouse) {
              currentPlanStore.setSpouseQuoteType(data.currentCoverageSelf)
              currentPlanStore.setSpouseCoveredByHealthInsurance(true)
            } else {
              currentPlanStore.setSpouseCoveredByHealthInsurance(true)
            }
            enableMainNext && enableMainNext()
          } else {
            setDisplayCard(false)
          }

          customerStore.set(data)
        })
        .catch((err) => {
          snackbarStore.set({
            snackbarMessage: getApiErrorMessage('fetch customer details'),
            snackbarOpen: true,
            snackbarType: SnackbarTypes.ERROR,
          })
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [forSpouse])

  const handleCurrentPlanFinish = () => {
    customerStore.setIsProfileUpdating(true)
    setState({
      ...state,
      nextButtonLoading: true,
    })
    let currentPlan = {} as {
      effectiveDate: null | string
      quoteType: string
      planType: string
      provider: string
      maPlanBenefits?: string[]
      spouseMAPlanBenefits?: string[]
    }

    if (!forSpouse)
      currentPlan = {
        effectiveDate: isEmpty(currentPlanStore.get().effectiveDate)
          ? null
          : currentPlanStore.effectiveDate!.substring(0, 10),
        //anticipatedEndDate: currentPlanStore.get().anticipatedEndDate,
        quoteType: currentPlanStore.get().quoteType,
        planType: currentPlanStore.get().planType,
        provider: currentPlanStore.get().provider,
        maPlanBenefits: currentPlanStore.getMABenefits(),
      }
    else
      currentPlan = {
        effectiveDate: isEmpty(currentPlanStore.spouseEffectiveDate)
          ? null
          : currentPlanStore.spouseEffectiveDate!.substring(0, 10),
        //anticipatedEndDate: currentPlanStore.get().anticipatedEndDate,
        quoteType: currentPlanStore.spouseQuoteType,
        planType: currentPlanStore.spousePlanType,
        provider: currentPlanStore.spouseProvider,
        spouseMAPlanBenefits: currentPlanStore.getSpouseMABenefits(),
      }

    updateSupplementalQuestions(currentPlan, { 'for-spouse': forSpouse })
      .then(() => {
        updateInfoStep(InfoStepEnum.CURRENT_PLAN)
          .then(() => {
            if (!forSpouse) {
              customerStore.setSupplementalQuestionAnswers({
                currentCoverageSelf: currentPlan.quoteType,
                currentPlanTypeSelf: currentPlan.planType,
                currentProviderSelf: currentPlan.provider,
                currentMedicarePartBDateSelf: currentPlan.effectiveDate,
              })
              customerStore.setFiltersInfoStep({
                coverageSelf: currentPlan.quoteType,
                planTypeSelf: currentPlan.planType,
                providerSelf: currentPlan.provider,
              })
            } else if (forSpouse) {
              customerStore.setSpouseSupplementalQuestionAnswers({
                currentCoverageSpouse: currentPlan.quoteType,
                currentPlanTypeSpouse: currentPlan.planType,
                currentProviderSpouse: currentPlan.provider,
                currentMedicarePartBDateSpouse: currentPlan.effectiveDate,
              })
              customerStore.setSpouseFiltersInfoStep({
                coverageSpouse: currentPlan.quoteType,
                planTypeSpouse: currentPlan.planType,
                providerSpouse: currentPlan.provider,
              })
            }
            medicareAdvantageFilterQuoteStore.clearStore()
            msPlanFiltersStore.clearMSPlanFilterStore()
            mPartDPlansFilterStore.clearStore()
            snackbarStore.set({
              snackbarMessage: StringConstants.SNACKBAR_SAVE_SUCCESS_MSG,
              snackbarOpen: true,
              snackbarType: SnackbarTypes.SUCCESS,
            })
            if (customerStore.getSpouseOptIn() && !forSpouse) {
              navigate(
                `${RouteConstants.INFO_STEPS_CURRENT_PLAN}/?forSpouse=true`
              )
            } else {
              setCurrentPlanStep(CurrentPlanISComponentEnum.FINISHED)
            }
          })
          .catch((err) => {
            snackbarStore.set({
              snackbarMessage: 'Something went wrong',
              snackbarOpen: true,
              snackbarType: 'error',
            })
          })
      })
      .catch((err) => {
        snackbarStore.set({
          snackbarMessage: getApiErrorMessage(`save plan`),
          snackbarOpen: true,
          snackbarType: 'error',
        })
      })
      .finally(() => {
        customerStore.setIsProfileUpdating(false)
        setState({
          ...state,
          nextButtonLoading: false,
        })
      })
    // }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return loading ? (
    <BouncingDotsLoader />
  ) : (
    <Container>
      {currentPlanStep ===
      CurrentPlanISComponentEnum.OPTIONAL_QUOTE_QUESTION ? (
        changeComponent(currentPlanStep)
      ) : currentPlanStep !== CurrentPlanISComponentEnum.FINISHED ? (
        changeComponent(currentPlanStep)
      ) : (
        <RecommendedPlan />
      )}

      {currentPlanStep !== CurrentPlanISComponentEnum.FINISHED && (
        <BottomStepNavigator
          handleNext={() => {
            if (
              currentPlanStep ===
              CurrentPlanISComponentEnum.OPTIONAL_QUOTE_QUESTION
            )
              updateOptionalQuotes()
            else getHandleNext(currentPlanStep)
          }}
          handleBack={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' })
            getHandleBack(currentPlanStep)
          }}
          handleViewPlans={() => {
            navigate(RouteConstants.DASHBOARD)
          }}
          disableNext={disableNext}
          isLoading={state.nextButtonLoading}
          currentInfoStep='Current Plan'
          buttonText3={hasNext ? 'Next' : 'Finish'}
          currentSectionCount={6}
          currentQuestionCount={currentStepNumber}
          totalQuestionCount={totalSteps}
        />
      )}
    </Container>
  )
}

export default SecureComponent(observer(CurrentPlan))
